import { useEffect, useMemo } from 'react';
import { useQueryClient } from 'react-query';

import { useApiQuery } from '../../api/useApi';
import { usePermissions } from '../../contexts/PermissionContext';
import type { Customer } from '../../pages/cardex/api/useCustomer';
import type { Message } from '../../pages/cardex/api/useMessage';
import type { Reservation } from '../../pages/cardex/api/useReservation';
import type { Order } from '../../pages/products/api/useOrder';
import type { SurveyResponse } from '../../pages/surveys-responses/api/useSurveyResponses';

import { marquerConversationLue } from './useConversations';

/**
 * Le fil d'une conversation — #83, périmètre arrêté par #31.
 *
 * **Quatre sources, là où le web en lance onze** (`Chat.tsx:90-107`). #31 a mesuré
 * ce qui vit réellement dans les données et ce qui n'y est pas :
 *
 * | Gardé | Présence |
 * |---|---|
 * | canaux (`/get-messages`) | WhatsApp 4 614, e-mail 2 442, SMS 205, live chat 72 |
 * | réservation | 1 817 conversations, **81 %** |
 * | formulaire | 1 054, **47 %** |
 * | commande | 719, **32 %** (mais 4 221 lignes — presque six par conversation) |
 *
 * Écartés parce que **morts dans les données** : `call` 0 ligne dans toute la base,
 * `messenger` 0, `instagram` 0, `note` 6 lignes, `payment` 1 conversation,
 * `google_review` 7 rattachements sur 45 703 avis. Écartés aussi `webhook`,
 * `companion`, et l'activité de flow — celle-ci renvoie 16 159 lignes dont
 * l'interface web écarte la quasi-totalité (elle ne rend que `LAUNCH_FLOW`, dont
 * #31 a compté **zéro** occurrence).
 *
 * Ça compte directement : #24 a fixé un délai d'abandon court, et sept requêtes en
 * moins à l'ouverture, c'est ce qui décide si le fil s'affiche sur un réseau
 * d'hôtel.
 *
 * **Les deux sources gatées le restent** : le web n'appelle les formulaires que
 * sous `surveys.view` et les commandes que sous `products.view`
 * (`ChatThread.tsx:183-184`). Sans ces gardes, un agent d'entretien déclencherait
 * deux 403 à chaque conversation ouverte.
 *
 * Une **cinquième** requête existe et n'est pas cachée : `/customer-basics/{id}`,
 * pour nommer le client dans l'en-tête. Elle ne fait pas partie des onze sources
 * d'items comptées par #31 — c'est un appel distinct de `ChatThread` — et on ne
 * peut pas s'en passer : #20 fait arriver ici **par notification**, donc sans
 * avoir vu la liste et sans état passé par le routeur.
 */

/** Ce qu'une ligne du fil peut être. Pas de `flow_event` : #31 l'a mesuré à zéro. */
export type ElementFil =
    | { genre: 'message'; date: string; cle: string; message: Message }
    | { genre: 'reservation'; date: string; cle: string; reservation: Reservation }
    | { genre: 'formulaire'; date: string; cle: string; reponse: SurveyResponse }
    | { genre: 'commande'; date: string; cle: string; commande: Order };

export interface FilConversation {
    elements: ElementFil[];
    client: Customer | undefined;
    /** Bruts, pour le composeur : la joignabilité du live chat se lit dessus (#84). */
    messages: Message[];
    /** Brutes, pour le composeur : présélection de réservation des modèles (#19 §7). */
    reservations: Reservation[];
    isLoading: boolean;
    isError: boolean;
    recharger: () => void;
}

/**
 * Le fil reste vivant tant qu'on le regarde (audit 2026-09-23) : le temps réel du
 * web ne couvre ni l'e-mail, ni le SMS, ni WhatsApp. 30 s, jamais en arrière-plan.
 * Coût vérifié : un `UPDATE read_at` en masse sans événement de modèle, même règle
 * que le web, qui remarque lu à chaque retour au premier plan.
 */
const FIL_VIVANT = {
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: 'always' as const,
    // Pas de réessai (audit 2026-09-23, A3) : le rechargement de 30 s en est un, et
    // l'échec du premier chargement se dit tout de suite, avec « Réessayer ».
    retry: false,
};

/**
 * Un message est-il un **envoi automatique** ?
 *
 * `action_launch_id` est porté par la ligne de message elle-même — d'où le fait,
 * relevé par #31, que la bascule ne peut **rien** économiser en réseau : c'est un
 * filtrage d'affichage, pas de chargement.
 *
 * C'est une **origine**, pas un type : elle traverse tous les canaux. C'est la
 * raison pour laquelle #31 a écarté quinze bascules par type au profit d'une seule.
 *
 * **`!is_received` n'est pas décoratif.** Mesuré en base : **3 513 messages
 * ENTRANTS portent un `action_launch_id`**, soit 48 % des 7 333 reçus — la réponse
 * d'une cliente à un message de flow reste rattachée à ce lancement. Tester le
 * seul `action_launch_id` masquerait donc près de la moitié de ce que les clientes
 * ont écrit, dès qu'on active la bascule. C'est l'inverse exact de ce que #31
 * protège, et en pire : ne pas voir ce que la cliente a dit.
 */
export function estAutomatique(message: Message): boolean {
    return !message.is_received && Boolean(message.action_launch_id);
}

export function useFilConversation(customerId: string | undefined): FilConversation {
    const { hasPermission } = usePermissions();
    const queryClient = useQueryClient();

    const id = customerId ?? '';
    const peutVoirFormulaires = hasPermission('surveys.view');
    const peutVoirCommandes = hasPermission('products.view');

    const messages = useApiQuery<Message[]>(id ? `/get-messages/${id}` : '', {}, FIL_VIVANT);
    const client = useApiQuery<Customer>(id ? `/customer-basics/${id}` : '');
    const reservations = useApiQuery<Reservation[]>(id ? `/reservations-customer/${id}` : '');
    const formulaires = useApiQuery<SurveyResponse[]>(
        id && peutVoirFormulaires ? `/customer-survey-responses/${id}` : '',
    );
    // `orders-customer` s'écrit sans barre de tête côté web : on ne s'en écarte pas,
    // la clé React Query et le cache seraient alors différents pour la même route.
    const commandes = useApiQuery<{ data: Order[] }>(
        id && peutVoirCommandes ? `orders-customer/${id}?page=1&limit=100` : '',
    );

    const elements = useMemo<ElementFil[]>(() => {
        const tout: ElementFil[] = [];

        for (const message of messages.data ?? []) {
            tout.push({ genre: 'message', date: message.created_at, cle: `m${message.id}`, message });
        }

        // Les éléments de contexte sont intercalés À LEUR DATE, pas groupés en fin
        // de fil : le fil raconte le séjour, et répondre sans savoir si la personne
        // arrive ce soir ou est partie la semaine dernière n'est pas tenable (#31).
        for (const reservation of reservations.data ?? []) {
            const date = reservation.date_creation || reservation.date_arrival;
            if (date) tout.push({ genre: 'reservation', date, cle: `r${reservation.id}`, reservation });
        }

        for (const reponse of formulaires.data ?? []) {
            const date = reponse.answered_at || reponse.created_at;
            if (date) tout.push({ genre: 'formulaire', date, cle: `f${reponse.id}`, reponse });
        }

        for (const commande of commandes.data?.data ?? []) {
            if (commande.created_at && commande.id != null) {
                tout.push({ genre: 'commande', date: commande.created_at, cle: `c${commande.id}`, commande });
            }
        }

        return tout.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    }, [messages.data, reservations.data, formulaires.data, commandes.data]);

    /*
     * `/get-messages` marque lus côté serveur ; la liste garde sa donnée 5 min, et la
     * conversation lue restait en gras. On la corrige SUR PLACE, sans requête
     * (audit 2026-09-23, A1) : voir `marquerConversationLue`.
     *
     * Suivi de `messages.data` et non de `dataUpdatedAt` : le partage structurel de
     * React Query garde la même référence quand un rechargement de 30 s ne rapporte
     * rien de neuf. Une fois au premier chargement réussi, puis à chaque vrai
     * changement, jamais à chaque tic.
     */
    // Seulement après une réponse du serveur depuis l'ouverture (relecture M3) : le
    // fil peut sortir du cache (1 h), et c'est `/get-messages` qui marque lu, pas
    // l'affichage. Sans réponse, la conversation n'est pas lue côté serveur.
    // `isFetchedAfterMount` passe aussi à vrai après un ÉCHEC (React Query 3) : le
    // fil venu du cache reste affiché, mais rien n'a été marqué lu (relecture finale).
    const messagesConfirmes = messages.isFetchedAfterMount && !messages.isError;
    useEffect(() => {
        if (!id || messages.data === undefined || !messagesConfirmes) return;
        void marquerConversationLue(queryClient, id);
    }, [messages.data, messagesConfirmes, id, queryClient]);

    return {
        elements,
        client: client.data,
        messages: messages.data ?? [],
        reservations: reservations.data ?? [],
        // Les sources de contexte ne retiennent PAS l'affichage : le fil est
        // lisible dès que les messages sont là, le reste s'intercale ensuite.
        isLoading: messages.isLoading,
        isError: Boolean(messages.isError),
        recharger: () => void messages.refetch?.(),
    };
}
